import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { 
  Phone, Lock, ArrowLeft, CheckCircle, AlertCircle, Loader2, 
  Wand2, Copy, Check, MessageSquare, Brain, Workflow, BarChart3, 
  ExternalLink, KeyRound, Eye, EyeOff
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/utils';


const features = [
  {
    icon: Phone,
    title: 'Sistema PBX VoIP',
    description: 'Centralino cloud completo con IVR, code, gruppi e voicemail',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    icon: MessageSquare,
    title: 'CRM WhatsApp & SMS',
    description: 'Campagne multicanale con WhatsApp Business e SMS integrati',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  {
    icon: Brain,
    title: 'AI Voice Agent',
    description: 'Agenti vocali AI con trascrizione real-time e sentiment analysis',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  {
    icon: Workflow,
    title: 'Workflows Automation',
    description: 'Automazioni visive drag-drop per marketing e CRM',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  {
    icon: BarChart3,
    title: 'Analytics Real Time',
    description: 'Dashboard live con metriche chiamate, performance e KPI',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
];

export default function ResetPassword() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { toast } = useToast();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [generatedPwd, setGeneratedPwd] = useState('');
  const [copiedPwd, setCopiedPwd] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const passwordChecks = useMemo(() => [
    { label: 'Almeno 8 caratteri', valid: password.length >= 8 },
    { label: 'Una lettera maiuscola', valid: /[A-Z]/.test(password) },
    { label: 'Una lettera minuscola', valid: /[a-z]/.test(password) },
    { label: 'Un numero', valid: /[0-9]/.test(password) },
    { label: 'Un carattere speciale', valid: /[^A-Za-z0-9]/.test(password) },
  ], [password]);

  const allChecksPass = passwordChecks.every(c => c.valid);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const generatePassword = () => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    const special = '!@#$%^&*()_+-=';
    const all = upper + lower + digits + special;
    let pwd = '';
    pwd += upper[Math.floor(Math.random() * upper.length)];
    pwd += lower[Math.floor(Math.random() * lower.length)];
    pwd += digits[Math.floor(Math.random() * digits.length)];
    pwd += special[Math.floor(Math.random() * special.length)];
    for (let i = 4; i < 16; i++) {
      pwd += all[Math.floor(Math.random() * all.length)];
    }
    pwd = pwd.split('').sort(() => Math.random() - 0.5).join('');
    setGeneratedPwd(pwd);
    setCopiedPwd(false);
    setPassword(pwd);
    setConfirmPassword(pwd);
    setShowPassword(true);
    setApiErrors([]);
  };

  const copyGeneratedPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPwd);
      setCopiedPwd(true);
      setTimeout(() => setCopiedPwd(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = generatedPwd;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedPwd(true);
      setTimeout(() => setCopiedPwd(false), 2000);
    }
  };

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(`/api/${tenantSlug}/validate-reset-token?token=${token}`);
        const data = await response.json();
        setIsTokenValid(data.valid === true);
      } catch (error) {
        console.error('Token validation error:', error);
        setIsTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, tenantSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiErrors([]);

    if (!allChecksPass) {
      toast({
        title: "Password non valida",
        description: "La password non soddisfa tutti i requisiti di sicurezza.",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Errore",
        description: "Le password non coincidono.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`/api/${tenantSlug}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data: {
        success?: boolean;
        error?: string;
        details?: Array<{ message?: string }>;
      } = await response.json();

      if (data.success) {
        setIsSuccess(true);
        toast({
          title: "Password Reimpostata",
          description: "Ora puoi effettuare il login con la nuova password.",
        });
        setTimeout(() => {
          navigate(`/${tenantSlug}/login`);
        }, 3000);
      } else {
        if (data.details && Array.isArray(data.details)) {
          setApiErrors(data.details.map((d) => d.message ?? ''));
        } else {
          throw new Error(data.error || 'Errore durante il reset');
        }
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: getErrorMessage(error) || "Si è verificato un errore. Riprova.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground font-medium">Verifica del link in corso...</p>
        </div>
      </div>
    );
  }

  const renderRightPanel = () => {
    if (!token || !isTokenValid) {
      return (
        <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full 
                          bg-red-400/30 backdrop-blur-sm border border-red-300/30 shadow-lg mb-4">
              <AlertCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Link Non Valido</h2>
            <p className="text-sm text-white/80 mt-1">Il link di reset non è valido o è scaduto</p>
          </div>

          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <p className="text-white/90 text-sm text-center">
                Il link di reset password non è valido o è scaduto. I link scadono dopo 30 minuti.
              </p>
            </div>
            <Link to={`/${tenantSlug}/forgot-password`}>
              <Button className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 
                                hover:scale-[1.02] transition-all duration-200 shadow-lg">
                Richiedi Nuovo Link
              </Button>
            </Link>
            <Link to={`/${tenantSlug}/login`}>
              <Button variant="outline" className="w-full mt-2 border-white/30 text-white hover:bg-white/10">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Torna al Login
              </Button>
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-white/20 text-center">
            <span className="text-xs text-white/60">
              crafted by{' '}
              <a href="https://www.easydigitalgroup.it" target="_blank" rel="noopener noreferrer"
                className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-0.5 hover:underline">
                easydigitalgroup <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </span>
          </div>
        </div>
      );
    }

    if (isSuccess) {
      return (
        <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full 
                          bg-green-400/30 backdrop-blur-sm border border-green-300/30 shadow-lg mb-4">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Password Reimpostata</h2>
            <p className="text-sm text-white/80 mt-1">La tua password è stata aggiornata con successo</p>
          </div>

          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <p className="text-white/90 text-sm text-center">
                Verrai reindirizzato alla pagina di login tra pochi secondi...
              </p>
            </div>
            <Link to={`/${tenantSlug}/login`}>
              <Button className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 
                                hover:scale-[1.02] transition-all duration-200 shadow-lg">
                Vai al Login
              </Button>
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-white/20 text-center">
            <span className="text-xs text-white/60">
              crafted by{' '}
              <a href="https://www.easydigitalgroup.it" target="_blank" rel="noopener noreferrer"
                className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-0.5 hover:underline">
                easydigitalgroup <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl p-8 shadow-2xl">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full 
                        bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg mb-4">
            <KeyRound className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">Reimposta Password</h2>
          <p className="text-sm text-white/80 mt-1">Scegli una nuova password sicura</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-medium text-white">Nuova Password</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={generatePassword}
                className="h-7 px-2 text-xs gap-1 text-white/80 hover:text-white hover:bg-white/10"
                disabled={isLoading}
              >
                <Wand2 className="h-3.5 w-3.5" />
                Genera
              </Button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                className="bg-white/90 border-white/20 pl-10 pr-20 focus-visible:ring-white/50
                          text-gray-900 placeholder:text-gray-500 transition-all duration-200"
                placeholder="Inserisci la nuova password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (e.target.value !== generatedPwd) setGeneratedPwd('');
                  setApiErrors([]);
                }}
                disabled={isLoading}
              />
              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                {generatedPwd && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={copyGeneratedPassword}
                    className="h-7 w-7 p-0 hover:bg-gray-200/50"
                    title="Copia password"
                  >
                    {copiedPwd ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-gray-400" />}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  className="h-7 w-7 p-0 hover:bg-gray-200/50"
                >
                  {showPassword ? <EyeOff className="h-3.5 w-3.5 text-gray-400" /> : <Eye className="h-3.5 w-3.5 text-gray-400" />}
                </Button>
              </div>
            </div>
            {generatedPwd && (
              <p className="text-xs text-green-300">Password generata automaticamente. Copiala prima di procedere!</p>
            )}
          </div>

          {password.length > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 space-y-1.5">
              {passwordChecks.map((check, i) => (
                <div key={i} className="flex items-center gap-2">
                  {check.valid ? (
                    <Check className="h-3.5 w-3.5 text-green-300 flex-shrink-0" />
                  ) : (
                    <div className="h-3.5 w-3.5 rounded-full border border-white/40 flex-shrink-0" />
                  )}
                  <span className={`text-xs ${check.valid ? 'text-green-300' : 'text-white/60'}`}>
                    {check.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-white">Conferma Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                required
                minLength={8}
                className="bg-white/90 border-white/20 pl-10 pr-10 focus-visible:ring-white/50
                          text-gray-900 placeholder:text-gray-500 transition-all duration-200"
                placeholder="Conferma la nuova password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-gray-200/50"
              >
                {showConfirmPassword ? <EyeOff className="h-3.5 w-3.5 text-gray-400" /> : <Eye className="h-3.5 w-3.5 text-gray-400" />}
              </Button>
            </div>
            {confirmPassword.length > 0 && (
              <p className={`text-xs ${passwordsMatch ? 'text-green-300' : 'text-red-300'}`}>
                {passwordsMatch ? 'Le password coincidono' : 'Le password non coincidono'}
              </p>
            )}
          </div>

          {apiErrors.length > 0 && (
            <div className="bg-red-500/20 rounded-lg p-3 border border-red-300/30">
              {apiErrors.map((err, i) => (
                <p key={i} className="text-xs text-red-200">{err}</p>
              ))}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 
                      hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50"
            disabled={isLoading || !allChecksPass || !passwordsMatch}
          >
            {isLoading ? 'Reimpostazione...' : 'Reimposta Password'}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <Link 
            to={`/${tenantSlug}/login`}
            className="text-sm text-white/90 hover:text-white transition-colors font-medium hover:underline"
          >
            <ArrowLeft className="inline-block mr-1 h-4 w-4" />
            Torna al Login
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-white/20 text-center">
          <span className="text-xs text-white/60">
            crafted by{' '}
            <a href="https://www.easydigitalgroup.it" target="_blank" rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-0.5 hover:underline">
              easydigitalgroup <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <div className="flex flex-col lg:flex-row min-h-screen">

        <div className="lg:w-1/2 flex flex-col justify-center p-8 lg:p-12 xl:p-16">
          <div className="mb-8 lg:mb-12">
            <div className="flex items-center gap-3 mb-4">
              <img src="/logos/easyflow-full-v3.svg" alt="EasyFlow" className="h-[67px]" />
            </div>
            <p className="text-lg text-muted-foreground">
              La piattaforma AI-powered per la gestione completa delle comunicazioni aziendali
            </p>
          </div>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${feature.iconBg} flex items-center justify-center`}>
                  <feature.icon className={`h-6 w-6 ${feature.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            {renderRightPanel()}
          </div>
        </div>
      </div>
    </div>
  );
}
